import type { FullKeys, FullKeysRecord, RnaComplexProps } from "../App";

export type CustomDomain = {
  rnaComplexIndex : number,
  rnaMoleculeName : string,
  start : number,
  stop : number
};

export type VisibilityEntry = {
  id : string,
  keys : FullKeysRecord,
  displayKeys : FullKeysRecord,
  enabled : boolean,
  focus : boolean
};

export type DisplayedRange = {
  rnaComplexIndex : number,
  rnaComplexName : string,
  rnaMoleculeName : string,
  start : number,
  stop : number
};

export function getSceneConstraintCounts(rnaComplexProps : RnaComplexProps) {
  const colors = new Set<string>();
  let moleculeCount = 0;
  for (const singularRnaComplexProps of Object.values(rnaComplexProps)) {
    moleculeCount += Object.keys(singularRnaComplexProps.rnaMoleculeProps).length;
    for (const singularRnaMoleculeProps of Object.values(singularRnaComplexProps.rnaMoleculeProps)) {
      for (const { color } of Object.values(singularRnaMoleculeProps.nucleotideProps)) {
        colors.add(`${color?.red ?? 0},${color?.green ?? 0},${color?.blue ?? 0},${color?.alpha ?? 255}`);
      }
    }
  }
  return {
    complexCount : Object.keys(rnaComplexProps).length,
    moleculeCount,
    colorCount : colors.size
  };
}

export function isNucleotideInKeys(
  keys : FullKeysRecord,
  rnaComplexIndex : number,
  rnaMoleculeName : string,
  nucleotideIndex : number
) {
  return keys[rnaComplexIndex]?.[rnaMoleculeName]?.has(nucleotideIndex) ?? false;
}

export function canDragCustomSelection(
  selectedKeys : FullKeysRecord,
  frozenKeys : FullKeysRecord,
  fullKeys : FullKeys
) {
  return isNucleotideInKeys(
    selectedKeys,
    fullKeys.rnaComplexIndex,
    fullKeys.rnaMoleculeName,
    fullKeys.nucleotideIndex
  ) && !isNucleotideInKeys(
    frozenKeys,
    fullKeys.rnaComplexIndex,
    fullKeys.rnaMoleculeName,
    fullKeys.nucleotideIndex
  );
}

export function addNucleotideToKeys(
  keys : FullKeysRecord,
  rnaComplexIndex : number,
  rnaMoleculeName : string,
  nucleotideIndex : number
) {
  if (!(rnaComplexIndex in keys)) {
    keys[rnaComplexIndex] = {};
  }
  if (!(rnaMoleculeName in keys[rnaComplexIndex])) {
    keys[rnaComplexIndex][rnaMoleculeName] = new Set<number>();
  }
  keys[rnaComplexIndex][rnaMoleculeName].add(nucleotideIndex);
}

export function getAllNucleotideKeys(rnaComplexProps : RnaComplexProps) {
  const keys : FullKeysRecord = {};
  for (const [rnaComplexIndexAsString, singularRnaComplexProps] of Object.entries(rnaComplexProps)) {
    const rnaComplexIndex = Number.parseInt(rnaComplexIndexAsString);
    for (const [rnaMoleculeName, singularRnaMoleculeProps] of Object.entries(singularRnaComplexProps.rnaMoleculeProps)) {
      for (const nucleotideIndexAsString of Object.keys(singularRnaMoleculeProps.nucleotideProps)) {
        addNucleotideToKeys(keys, rnaComplexIndex, rnaMoleculeName, Number.parseInt(nucleotideIndexAsString));
      }
    }
  }
  return keys;
}

export function customDomainsToKeys(
  rnaComplexProps : RnaComplexProps,
  customDomains : Array<CustomDomain>
) {
  const keys : FullKeysRecord = {};
  for (const { rnaComplexIndex, rnaMoleculeName, start, stop } of customDomains) {
    const singularRnaMoleculeProps = rnaComplexProps[rnaComplexIndex]?.rnaMoleculeProps[rnaMoleculeName];
    if (singularRnaMoleculeProps === undefined || !Number.isInteger(start) || !Number.isInteger(stop)) {
      continue;
    }
    const minimum = Math.min(start, stop);
    const maximum = Math.max(start, stop);
    for (const nucleotideIndexAsString of Object.keys(singularRnaMoleculeProps.nucleotideProps)) {
      const nucleotideIndex = Number.parseInt(nucleotideIndexAsString);
      const displayedNucleotideIndex = nucleotideIndex + singularRnaMoleculeProps.firstNucleotideIndex;
      if (displayedNucleotideIndex >= minimum && displayedNucleotideIndex <= maximum) {
        addNucleotideToKeys(keys, rnaComplexIndex, rnaMoleculeName, nucleotideIndex);
      }
    }
  }
  return keys;
}

export function unionKeys(...records : Array<FullKeysRecord>) {
  const union : FullKeysRecord = {};
  for (const record of records) {
    for (const [rnaComplexIndexAsString, keysPerRnaComplex] of Object.entries(record)) {
      const rnaComplexIndex = Number.parseInt(rnaComplexIndexAsString);
      for (const [rnaMoleculeName, nucleotideIndices] of Object.entries(keysPerRnaComplex)) {
        for (const nucleotideIndex of nucleotideIndices) {
          addNucleotideToKeys(union, rnaComplexIndex, rnaMoleculeName, nucleotideIndex);
        }
      }
    }
  }
  return union;
}

export function complementKeys(
  rnaComplexProps : RnaComplexProps,
  visibleKeys : FullKeysRecord
) {
  const complement : FullKeysRecord = {};
  const allKeys = getAllNucleotideKeys(rnaComplexProps);
  for (const [rnaComplexIndexAsString, keysPerRnaComplex] of Object.entries(allKeys)) {
    const rnaComplexIndex = Number.parseInt(rnaComplexIndexAsString);
    for (const [rnaMoleculeName, nucleotideIndices] of Object.entries(keysPerRnaComplex)) {
      for (const nucleotideIndex of nucleotideIndices) {
        if (!isNucleotideInKeys(visibleKeys, rnaComplexIndex, rnaMoleculeName, nucleotideIndex)) {
          addNucleotideToKeys(complement, rnaComplexIndex, rnaMoleculeName, nucleotideIndex);
        }
      }
    }
  }
  return complement;
}

export function getEffectiveHiddenKeys(entries : Array<VisibilityEntry>) {
  return unionKeys(...entries.filter(({ enabled }) => enabled).map(({ keys }) => keys));
}

export function getDisplayedRanges(
  keys : FullKeysRecord,
  rnaComplexProps : RnaComplexProps
) {
  const ranges = new Array<DisplayedRange>();
  for (const [rnaComplexIndexAsString, keysPerRnaComplex] of Object.entries(keys)) {
    const rnaComplexIndex = Number.parseInt(rnaComplexIndexAsString);
    const singularRnaComplexProps = rnaComplexProps[rnaComplexIndex];
    if (singularRnaComplexProps === undefined) {
      continue;
    }
    for (const [rnaMoleculeName, nucleotideIndices] of Object.entries(keysPerRnaComplex)) {
      const singularRnaMoleculeProps = singularRnaComplexProps.rnaMoleculeProps[rnaMoleculeName];
      if (singularRnaMoleculeProps === undefined) {
        continue;
      }
      const sortedIndices = Array.from(nucleotideIndices)
        .filter((nucleotideIndex) => nucleotideIndex in singularRnaMoleculeProps.nucleotideProps)
        .sort((a, b) => a - b);
      if (sortedIndices.length === 0) {
        continue;
      }
      let start = sortedIndices[0];
      let stop = start;
      function addRange() {
        ranges.push({
          rnaComplexIndex,
          rnaComplexName : singularRnaComplexProps.name,
          rnaMoleculeName,
          start : start + singularRnaMoleculeProps.firstNucleotideIndex,
          stop : stop + singularRnaMoleculeProps.firstNucleotideIndex
        });
      }
      for (let i = 1; i < sortedIndices.length; i++) {
        const nucleotideIndex = sortedIndices[i];
        if (nucleotideIndex === stop + 1) {
          stop = nucleotideIndex;
        } else {
          addRange();
          start = nucleotideIndex;
          stop = nucleotideIndex;
        }
      }
      addRange();
    }
  }
  return ranges;
}

export function filterVisibleRnaComplexProps(
  rnaComplexProps : RnaComplexProps,
  hiddenKeys : FullKeysRecord,
  hideTextAnnotations : boolean
) {
  const filtered : RnaComplexProps = structuredClone(rnaComplexProps);
  for (const [rnaComplexIndexAsString, singularRnaComplexProps] of Object.entries(filtered)) {
    const rnaComplexIndex = Number.parseInt(rnaComplexIndexAsString);
    for (const [rnaMoleculeName, singularRnaMoleculeProps] of Object.entries(singularRnaComplexProps.rnaMoleculeProps)) {
      for (const nucleotideIndexAsString of Object.keys(singularRnaMoleculeProps.nucleotideProps)) {
        const nucleotideIndex = Number.parseInt(nucleotideIndexAsString);
        if (isNucleotideInKeys(hiddenKeys, rnaComplexIndex, rnaMoleculeName, nucleotideIndex)) {
          delete singularRnaMoleculeProps.nucleotideProps[nucleotideIndex];
        }
      }
      if (Object.keys(singularRnaMoleculeProps.nucleotideProps).length === 0) {
        delete singularRnaComplexProps.rnaMoleculeProps[rnaMoleculeName];
      }
    }
    for (const [rnaMoleculeName, basePairsPerRnaMolecule] of Object.entries(singularRnaComplexProps.basePairs)) {
      const sourceMolecule = singularRnaComplexProps.rnaMoleculeProps[rnaMoleculeName];
      if (sourceMolecule === undefined) {
        delete singularRnaComplexProps.basePairs[rnaMoleculeName];
        continue;
      }
      for (const [nucleotideIndexAsString, basePairsPerNucleotide] of Object.entries(basePairsPerRnaMolecule)) {
        const nucleotideIndex = Number.parseInt(nucleotideIndexAsString);
        if (!(nucleotideIndex in sourceMolecule.nucleotideProps)) {
          delete basePairsPerRnaMolecule[nucleotideIndex];
          continue;
        }
        const retainedBasePairs = basePairsPerNucleotide.filter(({ rnaMoleculeName, nucleotideIndex }) => (
          nucleotideIndex in (singularRnaComplexProps.rnaMoleculeProps[rnaMoleculeName]?.nucleotideProps ?? {})
        ));
        if (retainedBasePairs.length === 0) {
          delete basePairsPerRnaMolecule[nucleotideIndex];
        } else {
          basePairsPerRnaMolecule[nucleotideIndex] = retainedBasePairs;
        }
      }
    }
    for (const [rnaMoleculeName, singularRnaMoleculeProps] of Object.entries(singularRnaComplexProps.rnaMoleculeProps)) {
      for (const [nucleotideIndexAsString, singularNucleotideProps] of Object.entries(singularRnaMoleculeProps.nucleotideProps)) {
        const connector = singularNucleotideProps.sequenceConnectorToNext;
        if (connector === undefined) {
          continue;
        }
        const nucleotideIndex = Number.parseInt(nucleotideIndexAsString);
        const targetMoleculeName = connector.targetMoleculeName ?? rnaMoleculeName;
        const targetNucleotideIndex = connector.targetNucleotideIndex ?? nucleotideIndex + 1;
        if (!(targetNucleotideIndex in (singularRnaComplexProps.rnaMoleculeProps[targetMoleculeName]?.nucleotideProps ?? {}))) {
          delete singularNucleotideProps.sequenceConnectorToNext;
        }
      }
    }
    if (hideTextAnnotations) {
      delete singularRnaComplexProps.textAnnotations;
    }
    if (Object.keys(singularRnaComplexProps.rnaMoleculeProps).length === 0) {
      delete filtered[rnaComplexIndex];
    }
  }
  return filtered;
}
